import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createQuickCheckupRequest } from '../lib/api';
import { CheckupTag, CreateCheckupResponse } from '@companion-professional/dctypes';

export type CreatePetAndCheckupFormValues = {
  clinicId: string;
  clinicPetId: string;
  locationTag: CheckupTag;
  petName: string;
  species: string;
};

// useCreatePetAndCheckup is a hook that creates a new pet entry and a checkup request for it in one step.  It is used
// on the second page of the Quick Checkup flow when the entered clinic pet id doesn't exist in the system yet.
//
// It returns:
// - isPending: A boolean that indicates if the request is currently in progress.
// - error: An error object if the request failed.
// - createPetAndCheckup: Mutation function that creates the pet and checkup.
export const useCreatePetAndCheckup = () => {
  const queryClient = useQueryClient();

  const {
    mutateAsync: createPetAndCheckup,
    isPending,
    error
  } = useMutation({
    mutationFn: async (values: CreatePetAndCheckupFormValues): Promise<CreateCheckupResponse> => {
      // TODO: Currently hard-coding a single request because we currently only support one request.  Need to update
      //  this to support multiple requests and other types.
      const res = await createQuickCheckupRequest(
        values.clinicId,
        values.clinicPetId,
        [{ video_type: 'default' }],
        [values.locationTag],
        values.species,
        values.petName
      );
      await queryClient.invalidateQueries({
        queryKey: ['active-checkups']
      });
      return res;
    }
  });

  return {
    createPetAndCheckup,
    isPending,
    error
  };
};
